import type { DataLayerUser } from "@/lib/server/actions/get-datalayer-user";

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, unknown>[];
  _gtmInitialized?: boolean;
  _gtmPageLocation?: string;
  _gtmStepsTracked?: Record<string, boolean>;
  _gtmTrackedEvents?: Record<string, boolean>;
};

declare const window: WindowWithDataLayer;

export const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;
export const PAGE_ANALYTICS_READY_EVENT = "d2c:page-analytics-ready";
const PREVIOUS_URL_STORAGE_KEY = "gtm_previous_url";
let currentPageType = "";

/** Page type of the page the visitor is on, e.g. "homepage" or "contentpage". */
export const getCurrentPageType = () => currentPageType;

const setCurrentPageType = (type: string | undefined) => {
  currentPageType = (type ?? "").replace(/\s+/g, "").toLowerCase();
};

export interface EcommerceItem {
  item_id: string;
  item_name: string;
  item_variant_id?: string;
  index?: number;
  item_list_id?: string;
  item_list_name?: string;
  price?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_variant?: string;
  item_size?: string;
  item_stock?: string;
  currency?: string;
  coupon?: string;
  discount?: number;
  quantity?: number;
}

interface VirtualPageViewData {
  pageUrl: string;
  country: string | string[];
  lang: string | string[];
  brand: string;
  type: string;
  division?: string;
}

function getSanitizedPageLocation(): string {
  if (typeof window === "undefined") {
    return "";
  }

  const url = new URL(window.location.href);

  url.searchParams.delete("email");

  return url.toString();
}

function updatePageLocation() {
  const pageLocation = getSanitizedPageLocation();
  if (window._gtmPageLocation === pageLocation) {
    return;
  }

  // GTM accepts gtag commands only as Arguments objects.
  function gtag(
    _command: "set",
    _fieldName: "page_location",
    _fieldValue: string
  ) {
    // biome-ignore lint/complexity/noArguments: GTM only accepts an Arguments object for gtag commands
    window.dataLayer.push(arguments as unknown as Record<string, unknown>);
  }

  gtag("set", "page_location", pageLocation);
  window._gtmPageLocation = pageLocation;
}

export const initializeGTM = () => {
  if (typeof window === "undefined" || window._gtmInitialized) {
    return;
  }

  window.dataLayer = window.dataLayer || [];

  // Consent defaults are owned by the GTM Usercentrics template.
  updatePageLocation();
  window._gtmInitialized = true;
};

export const pageviewToDataLayer = (
  country: string | string[],
  lang: string | string[],
  brand: string,
  type: string,
  division?: string,
  user?: DataLayerUser
) => {
  if (
    typeof window === "undefined" ||
    typeof window.dataLayer === "undefined"
  ) {
    return;
  }

  setCurrentPageType(type);
  updatePageLocation();

  const page = {
    country: country ?? "",
    language: lang ?? "",
    brand: brand ?? "",
    type: type ?? "",
    ...(division ? { division } : {}),
  };

  pushEvent("dataLayer_ready", {
    page,
    ...(user ? { user } : {}),
  });
  // The GTM bootstrap waits for this on routes whose metadata must precede
  // its initial page view. The event follows the dataLayer push.
  window.dispatchEvent(new Event(PAGE_ANALYTICS_READY_EVENT));
};

/**
 * Pushes an updated `user` object without a page view, so GTM Data Layer
 * Variables (user.id, user.login_method, user.login_status) reflect a login or
 * logout that happened without a full page load.
 */
export const userToDataLayer = (user: DataLayerUser) => {
  if (
    typeof window === "undefined" ||
    typeof window.dataLayer === "undefined"
  ) {
    return;
  }

  pushEvent("user_data_update", { user });
};

export const virtualPageviewToDataLayer = (data: VirtualPageViewData) => {
  if (typeof window === "undefined") {
    return;
  }

  setCurrentPageType(data.type);

  const previousUrl =
    window.sessionStorage.getItem(PREVIOUS_URL_STORAGE_KEY) ?? "";

  pushEvent("virtual_page_view", {
    pageUrl: data.pageUrl,
    pageTitle: typeof document !== "undefined" ? document.title : "",
    previousUrl,
    page: {
      country: data.country ?? "",
      language: data.lang ?? "",
      brand: data.brand ?? "",
      type: data.type ?? "",
      ...(data.division ? { division: data.division } : {}),
    },
  });

  window.sessionStorage.setItem(PREVIOUS_URL_STORAGE_KEY, data.pageUrl);
};

export const viewItemListToDataLayer = (
  items: EcommerceItem[],
  listName: string,
  listId?: string
) => {
  if (!items.length) {
    return;
  }

  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({ ecommerce: null });
  }

  pushEvent("view_item_list", {
    ecommerce: {
      item_list_name: listName,
      ...(listId ? { item_list_id: listId } : {}),
      items,
    },
  });
};

export const selectItemToDataLayer = (
  item: EcommerceItem,
  listName: string,
  listId?: string
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({ ecommerce: null });
  }

  pushEvent("select_item", {
    ecommerce: {
      item_list_name: listName,
      ...(listId ? { item_list_id: listId } : {}),
      items: [item],
    },
  });
};

/**
 * Builds the `ecommerce` block shared by single-item events. `value` is the unit
 * price times the quantity (one unit when the item carries no quantity, i.e.
 * `view_item`), and the event-level currency mirrors the item's.
 */
function singleItemEcommerce(item: EcommerceItem) {
  const quantity = item.quantity ?? 1;

  return {
    currency: item.currency ?? "",
    value: (item.price ?? 0) * quantity,
    items: [item],
  };
}

/** Reports a product detail page view. Fire once per PDP load. */
export const viewItemToDataLayer = (item: EcommerceItem) => {
  if (typeof window.dataLayer === "undefined") {
    return;
  }

  window.dataLayer.push({ ecommerce: null });
  pushEvent("view_item", { ecommerce: singleItemEcommerce(item) });
};

/** Reports a successful add to cart. Fire only after the cart update succeeds. */
export const addToCartToDataLayer = (item: EcommerceItem) => {
  if (typeof window.dataLayer === "undefined") {
    return;
  }

  window.dataLayer.push({ ecommerce: null });
  pushEvent("add_to_cart", { ecommerce: singleItemEcommerce(item) });
};

// Generated by GitHub Copilot - Oct-21-2026
export interface PromotionData {
  creative_name?: string;
  creative_slot: string;
  location_id: string;
  promotion_name: string;
}

export type PromotionClickData = {
  click_element: "CTA";
  click_element_name: string;
  link_target?: string;
  link_target_type?: string;
};

const pushPromotionEvent = (
  event: "view_promotion" | "select_promotion",
  promotion: PromotionData,
  extra: Record<string, unknown> = {}
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({ ecommerce: null });
  }

  pushEvent(event, { ecommerce: promotion, ...extra });
};

/** Reports that a promotion (hero, slide, campaign tile) became visible. */
export const viewPromotionToDataLayer = (promotion: PromotionData) => {
  pushPromotionEvent("view_promotion", promotion);
};

/** Reports a click on a promotion's call-to-action. */
export const selectPromotionToDataLayer = (
  promotion: PromotionData,
  click: PromotionClickData
) => {
  pushPromotionEvent("select_promotion", promotion, click);
};

/**
 * Reports a manual carousel interaction (dot, arrow or tab click).
 * Autoplay rotation must never call this.
 */
export const carouselSelectContentToDataLayer = (
  data: Record<string, unknown>
) => {
  pushEvent("select_content", { content_type: "carousel", ...data });
};

export const videoStartToDataLayer = (videoTitle: string, videoUrl: string) => {
  pushEvent("video_start", {
    video_title: videoTitle,
    video_url: videoUrl,
  });
};

export const videoProgressToDataLayer = (
  videoTitle: string,
  videoUrl: string,
  progressPercent: number,
  currentTime: number,
  duration: number
) => {
  pushEvent("video_progress", {
    video_title: videoTitle,
    video_url: videoUrl,
    video_percent: progressPercent,
    video_current_time: currentTime,
    video_duration: duration,
  });
};

export const videoCompleteToDataLayer = (
  videoTitle: string,
  videoUrl: string,
  duration: number
) => {
  pushEvent("video_complete", {
    video_title: videoTitle,
    video_url: videoUrl,
    video_duration: duration,
  });
};

export const registrationAccessToDataLayer = (
  country: string | string[],
  lang: string | string[],
  source: string
) => {
  if (typeof window.dataLayer !== "undefined") {
    pushEvent("bike_registration_link", {
      page: {
        country: country ?? "",
        language: lang ?? "",
        source,
      },
    });
  }
};

export const registrationStartToDataLayer = (
  country: string | string[],
  lang: string | string[],
  brand: string,
  division: string
) => {
  if (typeof window.dataLayer !== "undefined") {
    pushEvent("product_registration_start", {
      page: {
        country: country ?? "",
        language: lang ?? "",
        brand: brand ?? "",
        division: division ?? "",
      },
      user: {
        id: "",
        login_status: "",
        login_method: "",
      },
    });
  }
};

export const registrationSucceededToDataLayer = (
  country: string | string[],
  lang: string | string[],
  brand: string,
  division: string
) => {
  pushEvent(
    "product_registration_success",
    {
      page: {
        country: country ?? "",
        language: lang ?? "",
        brand: brand ?? "",
        division: division ?? "",
      },
      user: {
        id: "",
        login_status: "",
        login_method: "",
      },
    },
    { once: true }
  );
};

export const pushEvent = (
  event: string,
  data: Record<string, unknown>,
  options?: { once?: boolean }
) => {
  if (typeof window.dataLayer === "undefined") {
    return;
  }

  window._gtmTrackedEvents = window._gtmTrackedEvents || {};

  if (options?.once) {
    if (window._gtmTrackedEvents[event]) {
      return;
    }
    window._gtmTrackedEvents[event] = true;
  }

  window.dataLayer.push({ event, ...data });

  if (process.env.NODE_ENV === "development") {
    // Development logging can be added here if needed
  }
};

export const registrationStep2ToDataLayer = (
  country: string | string[],
  lang: string | string[],
  brand: string,
  division: string
) => {
  if (typeof window.dataLayer !== "undefined") {
    window._gtmStepsTracked = window._gtmStepsTracked || {};

    if (window._gtmStepsTracked.step2) {
      return;
    }

    pushEvent("product_registration_step2", {
      page: {
        country: country ?? "",
        language: lang ?? "",
        brand: brand ?? "",
        division: division ?? "",
      },
    });

    window._gtmStepsTracked.step2 = true;
  }
};

export const registrationStep3ToDataLayer = (
  country: string | string[],
  lang: string | string[],
  brand: string,
  division: string
) => {
  if (typeof window.dataLayer !== "undefined") {
    window._gtmStepsTracked = window._gtmStepsTracked || {};

    if (window._gtmStepsTracked.step3) {
      return;
    }

    pushEvent("product_registration_step3", {
      page: {
        country: country ?? "",
        language: lang ?? "",
        brand: brand ?? "",
        division: division ?? "",
      },
    });

    window._gtmStepsTracked.step3 = true;
  }
};

export type NavigationClickData = {
  contentId: string;
  navType: "header" | "footer" | "mega_menu" | "mobile_menu";
  navLevel: number;
  navIndex: number;
  navText: string;
  linkType?: "internal" | "external";
  linkUrl?: string;
};

export const navigationClickToDataLayer = (data: NavigationClickData) => {
  pushEvent("select_content", {
    content_type: "navigation",
    content_id: data.contentId,
    nav_type: data.navType,
    nav_level: data.navLevel,
    nav_index: data.navIndex,
    nav_text: data.navText,
    ...(data.linkType ? { link_type: data.linkType } : {}),
    page_type: currentPageType,
    ...(data.linkUrl ? { link_url: data.linkUrl } : {}),
  });
};
